'use client';
import { useRouter } from '@bprogress/next';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import type { ComboBoxOption } from '@ui/ComboBox';
import { addUserBike } from '@util/firestore/users';
import { getTypesenseClient } from '@util/getTypesense';
import useDebounce from '@util/hooks/useDebounce';
import { rockyTypeToMXType } from '@util/index';
import { logError } from '@util/logError';
import { useAuth } from 'context/AuthContext';
import { TemporaryBike, useBikeFilter } from 'context/BikeFilterContext';
import {
  ArrowLeftIcon,
  CheckIcon,
  ChevronRightIcon,
  HelpCircleIcon,
  XIcon,
} from 'lucide-react';
import { type Make, type UserBike, type Vehicle } from 'models/shared';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SearchResponseHit } from 'typesense/lib/Typesense/Documents';
import { SearchParams } from 'typesense/lib/Typesense/Types';
import { FitmentVehicleCard } from './FitmentVehicleCard';
import HelpWithVehicleModal from './HelpWithVehicleModal';
import VehicleOptionSkeleton from './VehicleOptionSkeleton';

type SelectionStep = 'search' | 'selectYear';

type PartialVehicleData = {
  make: string;
  model: string;
  type: Vehicle['type'];
};

type YearOption = {
  year: number;
  id: string;
  label: string;
};

type SearchByVehicleFormProps = {
  onBikeComplete: (bike: UserBike, fromGarage?: boolean) => void;
  onDismiss: () => void;
  className?: string;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  requireConfirmation?: boolean;
  temporaryBike?: TemporaryBike | null;
  setTemporaryBike?: (bike: TemporaryBike | null) => void;
  hideGarage?: boolean;
};

// const extractMake = (searchQueryText: string) => {
/** Shorthand / alternate spellings users type → canonical `Make` in `MAKE_ARRAY`. */
// const MAKE_SEARCH_ALIASES: { alias: string; make: Make }[] = (() => {
//   const rows: { alias: string; make: Make }[] = [
//     { alias: 'harley davidson', make: 'Harley Davidson' },
//     { alias: 'harley-davidson', make: 'Harley Davidson' },
//     { alias: 'harley', make: 'Harley Davidson' },
//     { alias: 'can-am', make: 'Can-Am' },
//     { alias: 'can am', make: 'Can-Am' },
//     { alias: 'canam', make: 'Can-Am' },
//   ];
//   rows.sort((a, b) => b.alias.length - a.alias.length);
//   return rows;
// })();

// // Match make only as a complete leading token (longest first).
// // Avoids substring false positives like "MZ" inside "RMZ250".
// const startsWithCompleteToken = (text: string, token: string) => {
//   const textLower = text.toLowerCase();
//   const tokenLower = token.toLowerCase();
//   if (!textLower.startsWith(tokenLower)) return false;
//   const nextChar = text[token.length];
//   return nextChar === undefined || !/[a-zA-Z0-9]/.test(nextChar);
// };

// const makesByLength = [...MAKE_ARRAY].sort((a, b) => b.length - a.length);
// let extractedMake: string | null = null;
// for (const make of makesByLength) {
//   if (startsWithCompleteToken(searchQueryText, make)) {
//     extractedMake = make;
//     searchQueryText = searchQueryText
//       .slice(make.length)
//       .replace(/\s+/g, ' ')
//       .trim();
//     break;
//   }
// }

// if (extractedMake == null) {
//   for (const { alias, make } of MAKE_SEARCH_ALIASES) {
//     if (startsWithCompleteToken(searchQueryText, alias)) {
//       extractedMake = make;
//       searchQueryText = searchQueryText
//         .slice(alias.length)
//         .replace(/\s+/g, ' ')
//         .trim();
//       break;
//     }
//   }
// }

// const normalizedRest = normalize(searchQueryText);
// const makeNormalized =
//   extractedMake != null && extractedMake !== ''
//     ? `${normalize(extractedMake)}-${normalizedRest}`.trim()
//     : normalizedRest;
// const searchParams: SearchParams<Vehicle, 'off'> = {
//   q: makeNormalized,
// query_by: 'normalized_name',
// }

export default function SearchByVehicleForm({
  onBikeComplete,
  onDismiss,
  className = '',
  searchQuery,
  setSearchQuery,
  requireConfirmation = false,
  temporaryBike,
  setTemporaryBike,
  hideGarage = false,
}: SearchByVehicleFormProps) {
  const [step, setStep] = useState<SelectionStep>('search');
  const [vehicleOptions, setVehicleOptions] = useState<
    (ComboBoxOption & { hit: SearchResponseHit<Vehicle> })[]
  >([]);
  const [yearOptions, setYearOptions] = useState<YearOption[]>([]);
  const [helpWithVehicleModalOpen, setHelpWithVehicleModalOpen] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [extractedYear, setExtractedYear] = useState<number | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showSearch, setShowSearch] = useState(false);
  const { userDoc } = useAuth();
  const { selectedBike, setSelectedBike } = useBikeFilter();
  const router = useRouter();
  const pathname = usePathname();
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const isVehicleSelected = (vehicleData: PartialVehicleData) => {
    return (
      vehicleData.make === selectedBike.make &&
      vehicleData.model === selectedBike.model
    );
  };

  const isYearSelected = (year: number) => {
    return year === selectedBike.year;
  };

  const searchVehicles = useCallback(async (q: string) => {
    if (!q?.trim()) {
      setVehicleOptions([]);
      setIsLoading(false);
      setExtractedYear(null);
      return;
    }

    setIsLoading(true);

    try {
      let searchQueryText = q.trim();

      let extractedYear: number | null = null;

      const yearIndex = searchQueryText.search(/\d{4}/g);

      if (yearIndex !== -1) {
        const yearMatch = searchQueryText.match(/\d{4}/g);

        if (yearMatch) {
          const year = parseInt(yearMatch[0]);

          if (year >= 1950 && year < 2100) {
            extractedYear = year;

            if (yearIndex === 0) {
              searchQueryText = searchQueryText.substring(4).trim();
            } else {
              searchQueryText = searchQueryText.replace(/\d{4}/, '').trim();
            }
          }
        }
      }

      setExtractedYear(extractedYear);

      // no longer extracting make, just search make,model
      // const searchParams = extractMake(searchQueryText);

      const searchParams: SearchParams<Vehicle, 'off'> = {
        q: searchQueryText,
        query_by: 'make,model,normalized_names',
        per_page: 25,
        exhaustive_search: true,
        sort_by: '_text_match:desc,popularity:desc',
      };

      // Add year filter if extracted from search query
      if (extractedYear) {
        searchParams.filter_by = `years:${extractedYear}`;
      }

      const res = await getTypesenseClient<Vehicle>('vehicles_alias', 'long')
        .documents()
        .search(searchParams);

      const uniqueVehicles = new Map<
        string,
        {
          make: string;
          model: string;
          type: Vehicle['type'];
          hit: SearchResponseHit<Vehicle>;
        }
      >();

      // adding hit documents to unique vehicles map
      res.hits?.forEach((hit) => {
        const vehicle = hit.document;
        const key = `${vehicle.make}-${vehicle.model}-${vehicle.type}`;

        if (!uniqueVehicles.has(key)) {
          uniqueVehicles.set(key, {
            make: vehicle.make,
            model: vehicle.model,
            type: vehicle.type,
            hit,
          });
        }
      });

      const options: (ComboBoxOption & { hit: SearchResponseHit<Vehicle> })[] =
        Array.from(uniqueVehicles.entries()).map(([key, vehicleData]) => {
          const label = extractedYear
            ? `${extractedYear} ${vehicleData.make} ${vehicleData.model}`.trim()
            : `${vehicleData.make} ${vehicleData.model}`.trim();

          return {
            id: key,
            label,
            value: JSON.stringify({
              ...vehicleData,
              displayYear: extractedYear,
            }),
            hit: vehicleData.hit,
          };
        });

      setVehicleOptions(options);
    } catch (error) {
      logError(error, 'Error searching vehicles:');
      setVehicleOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debouncedSearchQuery && step === 'search') {
      searchVehicles(debouncedSearchQuery);
    } else {
      setVehicleOptions([]);
      setIsLoading(false);
    }
  }, [debouncedSearchQuery, step, searchVehicles]);

  const handleVehicleSelect = async (option: {
    hit: SearchResponseHit<Vehicle>;
    id: string;
    label: string;
    value: string;
  }) => {
    try {
      const vehicleData: PartialVehicleData = JSON.parse(option.value);

      if (setTemporaryBike) {
        setTemporaryBike({
          make: vehicleData.make as Make,
          model: vehicleData.model,
          type: rockyTypeToMXType(vehicleData.type) ?? 'Dirt Bikes',
          year: null,
          date_added: null,
          downloadUrl: '',
          imageUrl: '',
          in_feed: false,
          allYears: option.hit.document.years,
        });
      } else {
        setSelectedBike({
          make: vehicleData.make as Make,
          model: vehicleData.model,
          type: rockyTypeToMXType(vehicleData.type),
          year: null,
          date_added: null,
          downloadUrl: '',
          imageUrl: '',
          in_feed: false,
          allYears: option.hit.document.years,
        });
      }

      const yearOptions =
        option.hit.document.years?.map((year) => ({
          year,
          id: `${vehicleData.make}-${vehicleData.model}-${year}`,
          label: `${year}`,
        })) ?? [];

      setYearOptions(yearOptions);

      if (extractedYear) {
        const res = await getTypesenseClient<Vehicle>('vehicles_alias', 'long')
          .documents()
          .search({
            q: `${vehicleData.make} ${vehicleData.model}`.trim(),
            query_by: 'make,model',
            filter_by: `make:=\`${vehicleData.make}\` && model:=\`${vehicleData.model}\``,
            per_page: 25,
            exhaustive_search: true,
          });

        const availableYears = new Set<number>();
        res.hits?.forEach((hit) => {
          const vehicle = hit.document;
          if (vehicle.years) {
            vehicle.years.forEach((year: number) => {
              availableYears.add(year);
            });
          }
        });

        if (availableYears.has(extractedYear)) {
          const completeVehicle: UserBike = {
            make: vehicleData.make as Make,
            model: vehicleData.model,
            year: extractedYear,
            date_added: null,
            downloadUrl: '',
            imageUrl: '',
            in_feed: false,
            type: rockyTypeToMXType(vehicleData.type) ?? 'Dirt Bikes',
          };

          if (
            !completeVehicle.make ||
            !completeVehicle.model ||
            !completeVehicle.year
          ) {
            setValidationError('Please complete all vehicle fields');
            return;
          }

          setValidationError(null);

          if (setTemporaryBike) {
            setTemporaryBike({
              ...temporaryBike,
              ...completeVehicle,
              allYears: option.hit.document.years,
            });
          } else {
            setSelectedBike({
              ...selectedBike,
              ...completeVehicle,
              allYears: option.hit.document.years,
            });
          }

          onBikeComplete(completeVehicle);
          if (
            completeVehicle &&
            userDoc?.uid &&
            !userDoc?.bikes?.some(
              (b) =>
                b.make === completeVehicle.make &&
                b.model === completeVehicle.model &&
                b.year === completeVehicle.year
            )
          ) {
            addUserBike(userDoc.uid, completeVehicle as UserBike);
          }
          if (!requireConfirmation) {
            onDismiss();
          }
          return;
        }
      }

      if (!extractedYear) {
        setStep('selectYear');
      }
    } catch (error) {
      logError(error, `Error parsing vehicle data: ${JSON.stringify(option)}`);
    }
  };

  const handleYearSelect = (yearOption: YearOption) => {
    const sourceBike = setTemporaryBike ? temporaryBike : selectedBike;

    if (!sourceBike) return;

    const completeVehicle = {
      make: sourceBike.make as Make,
      model: sourceBike.model ?? '',
      year: yearOption.year,
      date_added: null,
      downloadUrl: '',
      imageUrl: '',
      in_feed: false,
      type: sourceBike.type ?? 'Dirt Bikes',
      allYears: yearOptions.map((y) => y.year),
    };

    if (
      !completeVehicle.make ||
      !completeVehicle.model ||
      !completeVehicle.year
    ) {
      setValidationError('Please complete all vehicle fields');
      return;
    }

    setValidationError(null);

    if (setTemporaryBike) {
      setTemporaryBike({
        ...temporaryBike,
        ...completeVehicle,
      });
    } else {
      setSelectedBike({
        ...selectedBike,
        ...completeVehicle,
      });
    }

    onBikeComplete(completeVehicle);
    if (
      completeVehicle &&
      userDoc?.uid &&
      !userDoc?.bikes?.some(
        (b) =>
          b.make === completeVehicle.make &&
          b.model === completeVehicle.model &&
          b.year === completeVehicle.year
      )
    ) {
      addUserBike(userDoc.uid, completeVehicle as UserBike);
    }
    if (!requireConfirmation) {
      onDismiss();
    }
  };

  const handleBackToSearch = () => {
    setStep('search');
    setYearOptions([]);
  };

  const sortedVehicleOptions = [...vehicleOptions].sort((a, b) => {
    const vehicleA: PartialVehicleData = JSON.parse(a.value);
    const vehicleB: PartialVehicleData = JSON.parse(b.value);

    const isSelectedA = isVehicleSelected(vehicleA);
    const isSelectedB = isVehicleSelected(vehicleB);

    if (isSelectedA && !isSelectedB) return -1;
    if (!isSelectedA && isSelectedB) return 1;
    return 0;
  });

  const sortedYearOptions = [...yearOptions].sort((a, b) => {
    const isSelectedA = isYearSelected(a.year);
    const isSelectedB = isYearSelected(b.year);

    if (isSelectedA && !isSelectedB) return -1;
    if (!isSelectedA && isSelectedB) return 1;
    return b.year - a.year;
  });

  const handleTalkToSupport = async () => {
    if (!userDoc) {
      router.push(`/login?redirect=${encodeURIComponent(pathname ?? '')}`);
      return;
    }

    setHelpWithVehicleModalOpen(true);
  };
  const hasGarageBikes = userDoc?.bikes && userDoc.bikes.length > 0;
  const shouldShowGarage =
    hasGarageBikes && !showSearch && searchQuery === '' && !hideGarage;

  // Auto-show search if user starts typing
  useEffect(() => {
    if (searchQuery !== '') {
      setShowSearch(true);
    }
  }, [searchQuery]);

  // Initialize showSearch based on whether user has garage bikes
  useEffect(() => {
    if (!hasGarageBikes || hideGarage) {
      setShowSearch(true);
    } else {
      // If user has garage bikes, ensure we start at garage view
      setShowSearch(false);
    }
  }, [hasGarageBikes, hideGarage]);

  if (step === 'selectYear') {
    return (
      <div className={`flex flex-col gap-y-6 overflow-y-hidden ${className}`}>
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={handleBackToSearch}
            className="flex items-center gap-x-2 text-[1.6rem] font-medium text-zinc-600 hover:text-zinc-800"
          >
            <ArrowLeftIcon className="h-6 w-6" />
            Select other vehicle
          </button>
        </div>

        {selectedBike && (
          <div className="rounded-[1.5rem] bg-zinc-50 p-4">
            <p className="text-[1.6rem] text-zinc-600">
              Selected:{' '}
              <span className="font-semibold">
                {selectedBike.make} {selectedBike.model}
              </span>
            </p>
          </div>
        )}

        {yearOptions.length === 0 && (
          <div className="flex h-[30rem] w-full items-center justify-center">
            <div className="flex flex-col items-center gap-y-6">
              <span className="text-[1.8rem] font-medium text-zinc-400">
                No years found for this vehicle.
              </span>
              <button
                type="button"
                className="flex h-[5.5rem] items-center gap-x-2 rounded-full bg-brand-secondary px-[2rem] py-[1rem] text-[1.8rem] font-semibold text-white"
                onClick={handleTalkToSupport}
              >
                <HelpCircleIcon className="h-8 w-8 text-white" />
                Talk to Support
              </button>
            </div>
          </div>
        )}

        {yearOptions.length > 0 && (
          <div className="flex max-h-[45rem] w-full flex-col gap-y-6 overflow-y-scroll pb-8 sm:max-h-[50rem] sm:pb-48">
            {sortedYearOptions.map((yearOption) => {
              const isSelected = isYearSelected(yearOption.year);

              return (
                <button
                  type="button"
                  key={yearOption.id}
                  className={`duration-50 flex h-[6rem] w-full cursor-pointer items-center justify-between rounded-full px-[1.5rem] py-[1rem] text-left text-[1.8rem] font-medium text-zinc-800 transition-all ${
                    isSelected ? 'bg-red-50' : 'hover:bg-zinc-50 '
                  }`}
                  onClick={() => handleYearSelect(yearOption)}
                >
                  <span className="w-[30rem] capitalize leading-relaxed">
                    {yearOption.label}
                  </span>

                  {isSelected && (
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-brand-secondary p-[0.5rem]">
                      <CheckIcon className="h-full w-full text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <HelpWithVehicleModal
          isOpen={helpWithVehicleModalOpen}
          dismiss={() => setHelpWithVehicleModalOpen(false)}
          onCloseAllModals={() => {
            onDismiss();
            setHelpWithVehicleModalOpen(false);
          }}
        />
      </div>
    );
  }

  const handleBikeSelect = (bike: UserBike) => {
    const completeVehicle: UserBike = {
      make: bike.make as Make,
      model: bike.model ?? '',
      year: bike.year ?? new Date().getFullYear(),
      date_added: bike.date_added,
      downloadUrl: bike.downloadUrl,
      imageUrl: bike.imageUrl,
      in_feed: bike.in_feed,
      type: bike.type ?? 'Dirt Bikes',
    };

    if (
      !completeVehicle.make ||
      !completeVehicle.model ||
      !completeVehicle.year
    ) {
      setValidationError('Please complete all vehicle fields');
      return;
    }

    setValidationError(null);
    onBikeComplete(completeVehicle, true);
    onDismiss();
  };

  return (
    <div className={`flex flex-col gap-y-6 ${className}`}>
      {shouldShowGarage && (
        <div className="flex flex-col gap-y-4 pb-4">
          <div className="flex items-center gap-x-2">
            <h3 className="text-[2rem] font-normal text-black">My Garage</h3>
            <ChevronRightIcon className="h-6 w-6 text-zinc-500" />
          </div>

          <div className="flex max-h-[50rem] flex-col gap-y-4 overflow-y-scroll">
            {userDoc.bikes.map((bike, index) => {
              const isSelected =
                selectedBike?.make === bike.make &&
                selectedBike?.model === bike.model &&
                selectedBike?.year === bike.year;

              return (
                <FitmentVehicleCard
                  key={`garage-bike-${index}-${bike.make}-${bike.model}-${bike.year}`}
                  bike={bike}
                  isSelected={isSelected}
                  onClick={() => handleBikeSelect(bike)}
                />
              );
            })}
          </div>

          <div className="mt-6 border-t border-zinc-300 pt-6">
            <div className="flex flex-col gap-y-3">
              <button
                type="button"
                onClick={() => {
                  setShowSearch(true);
                  setTimeout(() => {
                    inputRef.current?.focus();
                  }, 100);
                }}
                className="flex h-[6rem] w-full items-center justify-center rounded-full border border-brand-secondary bg-white px-8 py-4 text-[1.8rem] font-semibold text-brand-secondary transition-colors hover:bg-brand-secondary/10"
              >
                Add new vehicle
              </button>
            </div>
          </div>
        </div>
      )}

      {(showSearch || !shouldShowGarage) && (
        <div className="flex w-full flex-col items-start gap-2">
          {hasGarageBikes && showSearch && !hideGarage && (
            <button
              type="button"
              onClick={() => {
                setShowSearch(false);
                setSearchQuery('');
              }}
              className="mb-2 flex items-center gap-x-2 text-[1.6rem] font-medium text-zinc-600 transition-colors hover:text-zinc-800"
            >
              <ArrowLeftIcon className="h-6 w-6" />
              Back to My Garage
            </button>
          )}
          <p className="text-[1.8rem] font-semibold text-zinc-500">
            Search by Year, Make & Model
          </p>

          <div className="flex h-[5.5rem] w-full items-center justify-between gap-x-8 overflow-y-hidden rounded-[1.5rem] border border-zinc-300 bg-zinc-100 px-[2rem] py-[1rem] focus-within:border-[2px] focus-within:border-zinc-700">
            <input
              ref={inputRef}
              type="text"
              placeholder="2024 Yamaha YZ250F"
              className="h-full w-full border-none bg-transparent text-[1.8rem] text-zinc-700 placeholder:text-zinc-400 focus:ring-0"
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setValidationError(null);
              }}
              value={searchQuery}
            />

            <div className="flex items-center gap-x-2">
              {searchQuery === '' ? (
                <MagnifyingGlassIcon className="h-10 w-10 text-zinc-500" />
              ) : (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearchQuery('')}
                  className="cursor-pointerF"
                >
                  <XIcon className="h-10 w-10 text-zinc-500" />
                </button>
              )}
            </div>
          </div>

          {validationError && (
            <p className="text-[1.4rem] text-red-600">{validationError}</p>
          )}
        </div>
      )}

      {isLoading && searchQuery !== '' && <VehicleOptionSkeleton count={5} />}

      {!isLoading && searchQuery === '' && (!hasGarageBikes || showSearch) && (
        <div className="flex h-[30rem] w-full items-center justify-center">
          <div className="flex flex-col items-center gap-y-6">
            <MagnifyingGlassIcon className="h-32 w-32 text-zinc-300 sm:h-24 sm:w-24" />

            <span className="text-[1.6rem] font-semibold text-zinc-400">
              Start typing to find your vehicle.
            </span>
          </div>
        </div>
      )}

      {!isLoading && searchQuery !== '' && !vehicleOptions.length && (
        <div className="flex h-[30rem] w-full items-center justify-center">
          <div className="flex flex-col items-center gap-y-6">
            <div className="flex flex-col items-center gap-y-2">
              <span className="text-[1.8rem] font-medium text-zinc-400">
                No results.
              </span>
              <span className="text-[1.8rem] font-medium text-zinc-400">
                Try another make or model
              </span>
            </div>

            <button
              type="button"
              className="flex h-[5.5rem] items-center gap-x-2 rounded-full bg-brand-secondary px-[2rem] py-[1rem] text-[1.8rem] font-semibold text-white"
              onClick={handleTalkToSupport}
            >
              <HelpCircleIcon className="h-8 w-8 text-white" />
              Talk to Support
            </button>
          </div>
        </div>
      )}

      {!isLoading && !!vehicleOptions.length && searchQuery !== '' && (
        <div className="flex max-h-[45rem] w-full flex-col gap-y-6 overflow-y-scroll pb-8 sm:max-h-[50rem] sm:pb-48">
          {sortedVehicleOptions.map((vehicle) => {
            const vehicleData: PartialVehicleData = JSON.parse(vehicle.value);
            const isSelected = isVehicleSelected(vehicleData);
            const hasSelectedYear = isSelected && selectedBike.year;

            return (
              <div key={vehicle.id} className="flex flex-col gap-y-2">
                <button
                  type="button"
                  className={`duration-50 flex h-[6rem] w-full cursor-pointer items-center justify-between rounded-full px-[1.5rem] py-[1rem] text-left text-[1.8rem] font-medium text-zinc-800 transition-all ${
                    isSelected ? 'bg-red-50' : 'hover:bg-zinc-50 '
                  }`}
                  onClick={() => {
                    handleVehicleSelect(vehicle);
                  }}
                >
                  <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap capitalize leading-relaxed [&_mark]:bg-transparent [&_mark]:font-semibold">
                    {extractedYear && (
                      <span className="font-semibold">{extractedYear} </span>
                    )}
                    {vehicle.hit.highlight?.make?.snippet ? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: vehicle.hit.highlight.make.snippet,
                        }}
                      />
                    ) : (
                      <span>{vehicle.hit.document.make}</span>
                    )}
                    <span> </span>
                    {vehicle.hit.highlight?.model?.snippet ? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: vehicle.hit.highlight.model.snippet,
                        }}
                      />
                    ) : (
                      <span>{vehicle.hit.document.model}</span>
                    )}
                  </span>

                  {isSelected && (
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-brand-secondary p-[0.5rem]">
                      <CheckIcon className="h-full w-full text-white" />
                    </div>
                  )}
                </button>

                {hasSelectedYear && (
                  <div className="flex items-center justify-between rounded-[1.5rem] bg-zinc-100 px-4 py-6">
                    <div className="flex items-center gap-x-3">
                      <span className="text-[1.6rem] text-zinc-600">
                        <span className="font-semibold">
                          {selectedBike.year}
                        </span>{' '}
                        selected
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setStep('selectYear');

                        setYearOptions(
                          selectedBike.allYears?.map((year) => ({
                            year,
                            id: `${vehicleData.make}-${vehicleData.model}-${year}`,
                            label: `${year}`,
                          })) ?? []
                        );
                      }}
                      className="text-[1.4rem] font-medium text-brand-secondary underline hover:text-brand-secondary/80 sm:text-[1.6rem]"
                    >
                      Edit year
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          <button
            type="button"
            className="duration-50 mt-6 w-full cursor-pointer rounded-[1.5rem] border border-zinc-200 bg-zinc-50 p-6 text-[1.8rem] text-zinc-600 transition-colors hover:bg-zinc-100"
            onClick={handleTalkToSupport}
          >
            Didn&apos;t find your vehicle?{' '}
            <span className="font-medium text-brand-secondary">
              Talk to support.
            </span>
          </button>
        </div>
      )}

      <HelpWithVehicleModal
        isOpen={helpWithVehicleModalOpen}
        dismiss={() => setHelpWithVehicleModalOpen(false)}
        onCloseAllModals={() => {
          onDismiss();
          setHelpWithVehicleModalOpen(false);
        }}
      />
    </div>
  );
}
